import store from 'store/configureStore';
import { withHandlers } from 'recompose';

import getBase64ImageSize from 'modules/user/utils/getBase64ImageSize';
import transformImageProportions from 'modules/user/utils/transformImageProportions';
import {
  invokeIncorrectExtensionModal,
  invokeUploadAvatarFailureModal,
  invokeFileSizeExceededModal,
} from '../actions/avatarUpload';

/**
 * Simple decrease avatar size operations
 *
 * - usually 130x130 images generate ~30 kb packed images,
 * - if needed to store images with higher size, investigate produced images size
 * - finally, if needed to decrease image size yet extend decreaseBase64ImageSize and decrease recursively quality of images to 0.9
 *
 */

const IMAGE_MAX_SIZE_MB = 1;
const IMAGE_SIZE_TO_TRANSFORM_IN_BYTES = 50 * 1000;
const EMPTY_IMAGE = 'data:';
const ACCEPT_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const decreaseBase64ImageSize = (inputImage, inputImageSize, maxWidth, maxHeight) => {
  return inputImageSize < IMAGE_SIZE_TO_TRANSFORM_IN_BYTES
    ? Promise.resolve({ logo: inputImage, meta: { originalSize: inputImageSize } })
    : transformImageProportions(inputImage, maxWidth, maxHeight);
};


const getFileExtensionFromFileName = fileName => {
  const re = /(?:\.([^.]+))?$/;

  return re.exec(fileName)[1] || '';
};

export const withAvatarUpload = withHandlers({
  handleAvatarUpload: ({ saveAvatar }) => event => {
    const dispatch = store.dispatch;
    const firstFile = event && event[0];
    if (!firstFile) {
      return;
    }

    const fileName = firstFile.name || '';
    const fileExtension = getFileExtensionFromFileName(fileName).toLowerCase();
    const isAvatarExtensionValid = ACCEPT_IMAGE_EXTENSIONS.some(extension => extension === fileExtension);

    if (!isAvatarExtensionValid) {
      return dispatch(invokeIncorrectExtensionModal(fileExtension));
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const avatar = reader.result;
      const avatarSize = getBase64ImageSize(avatar);

      if (avatar === EMPTY_IMAGE) {
        return dispatch(invokeUploadAvatarFailureModal());
      }

      if (avatarSize > IMAGE_MAX_SIZE_MB * (1024 ** 2)) {
        return dispatch(invokeFileSizeExceededModal(fileExtension, IMAGE_MAX_SIZE_MB));
      }

      decreaseBase64ImageSize(avatar, avatarSize, 130, 130)
        .then(saveAvatar)
        .catch(dispatch(invokeUploadAvatarFailureModal));
    };

    reader.readAsDataURL(firstFile);
  },

  removeAvatar: ({ saveAvatar }) => () => {
    saveAvatar({ logo: '', meta: { deleted: true } });
  },
});
